import React, { useEffect, useState } from 'react';

import classNames from 'classnames';

import CentralAssumptionsTableGroupRow from './CentralAssumptionsTableGroupRow';
import CentralAssumptionsTableRow from './CentralAssumptionsTableRow';

import { DragAndDrop, Drag, Drop } from 'CommonComponents/DragAndDrop';
import Tooltip from 'CommonComponents/Tooltip';

import CollapseAllIcon from 'Assets/icons/collapse-all.svg';
import ExpandAllIcon from 'Assets/icons/expand-all.svg';

const LOCAL_STORAGE_COLLAPSED_CATEGORIES_KEY = 'COLLAPSED_GROUPS';
const LOCAL_STORAGE_ANY_EXPANDED_CATEGORY_KEY = 'ANY_EXPANDED_GROUP';

const getPersistedCategoriesCollapsed = () => {
    return localStorage.getItem(LOCAL_STORAGE_COLLAPSED_CATEGORIES_KEY) ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_COLLAPSED_CATEGORIES_KEY)) : {};
};

const getPersistedAnyExpandedCategory = () => {
    return localStorage.getItem(LOCAL_STORAGE_ANY_EXPANDED_CATEGORY_KEY) ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_ANY_EXPANDED_CATEGORY_KEY)) : {};
};

export default function CentralAssumptionsTable({
    groups,
    loading,
    onAssumptionGroupEdit,
    onAssumptionGroupDelete,
    deletable,
    deletableRows,
    toggleDeletableRow,
    showSourceColumn,
    ...props
}) {
    const [categories, setCategories] = useState(groups);
    const [expandedCategories, setExpandedCategories] = useState(getPersistedCategoriesCollapsed());
    const [anyExpandedCategory, setAnyExpandedCategory] = useState(getPersistedAnyExpandedCategory());

    useEffect(() => {
        if (!loading && !!groups) {
            setCategories(groups);
            updateExpandedCategories(groups);
        }
    }, [loading]);

    useEffect(() => {
        if (!loading && !!groups) {
            updateExpandedCategories(groups);
        }
    }, [groups]);

    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_COLLAPSED_CATEGORIES_KEY, JSON.stringify(expandedCategories));
    }, [expandedCategories]);

    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_ANY_EXPANDED_CATEGORY_KEY, JSON.stringify(anyExpandedCategory));
    }, [anyExpandedCategory]);

    const refreshCentralAssumptions = () => {
        props.fetchCentralAssumptions();
    };

    const updateExpandedCategories = (dataCategories) => {
        if (dataCategories) {
            let expanded = JSON.parse(JSON.stringify(expandedCategories));
            let anyExpandedCategory = false;
            dataCategories.forEach((category) => {
                const categoryId = category.id;

                expanded[categoryId] = expanded[categoryId] || {
                    expanded: true,
                };

                if (!anyExpandedCategory) {
                    anyExpandedCategory = expanded[categoryId].expanded;
                }
            });

            setAnyExpandedCategory(anyExpandedCategory);
            setExpandedCategories(expanded);
        }
    };

    const toggleExpanded = (categoryId) => {
        const expanded = JSON.parse(JSON.stringify(expandedCategories));
        expanded[categoryId].expanded = !expanded[categoryId].expanded;
        setExpandedCategories(expanded);
    };

    const toggleAllExpanded = (expandedVal = false, categoryId = null) => {
        const expanded = JSON.parse(JSON.stringify(expandedCategories));
        for (const key in expanded) {
            if (categoryId) {
                let subcategories = expanded[categoryId].subcategories;
                for (const subcategory in subcategories) {
                    expanded[categoryId].subcategories[subcategory].expanded = expandedVal;
                }
                expanded[categoryId].anyExpandedSubCategory = expandedVal;
                expanded[categoryId].expanded = expandedVal;
            } else {
                expanded[key].expanded = expandedVal;
            }
        }

        if (!categoryId) {
            setAnyExpandedCategory(expandedVal);
        }

        setExpandedCategories(expanded);
    };

    const updateCategoriesOrder = (updatedCategories) => {
        const url = props.updateCategoriesOrder;
        const data = { categories: updatedCategories };
        const axiosPromise = axios
            .put(url, data)
            .then((res) => {
                refreshCentralAssumptions();
            })
            .catch((e) => {
                console.log(e?.response);
            });

        // loaderInstance.trackAxiosRequest(axiosPromise);
    };

    const handleDragEnd = (result) => {
        const { type, source, destination } = result;

        if (!destination) return;

        const sourceIdParts = source.droppableId.split('-');
        const sourceIndex = source.index;

        var sourceCategoryId = null;

        const destinationIdParts = destination.droppableId.split('-');
        const destinationIndex = destination.index;

        var destinationCategoryId = null;

        const draggableParts = result.draggableId.split('-');

        if (type == 'droppable-category-activities') {
            sourceCategoryId = parseInt(sourceIdParts[1]);
            destinationCategoryId = parseInt(destinationIdParts[1]);
        } else {
            sourceCategoryId = parseInt(sourceIdParts[1]);
            destinationCategoryId = parseInt(destinationIdParts[1]);
        }

        // Reordering activities
        if (type === 'droppable-category-activities') {
            const sourceCategoryIndex = categories.findIndex((category) => category.id === sourceCategoryId);
            const destinationCategoryIndex = categories.findIndex((category) => category.id === destinationCategoryId);

            const sourceCategory = categories[sourceCategoryIndex];
            const destinationCategory = categories[destinationCategoryIndex];

            const draggableActivityId = parseInt(draggableParts[3]);
            const draggableActivity = sourceCategory.assumptions.find((activity) => activity.id === draggableActivityId);

            if (sourceCategoryId === destinationCategoryId) {
                // Reordering activities within the same category
                const updatedActivities = reorder(sourceCategory.assumptions, sourceIndex, destinationIndex);
                const updatedCategory = { ...sourceCategory, assumptions: updatedActivities };
                const updatedCategories = [...categories];
                updatedCategories[sourceCategoryIndex] = updatedCategory;
                setCategories(updatedCategories);
                updateCategoriesOrder(updatedCategories);
                updateExpandedCategories(updatedCategories);
            } else {
                // Moving activity between categories
                const updatedSourceActivities = sourceCategory.assumptions.filter((activity) => activity.id !== draggableActivityId);
                const updatedDestinationActivities = insertItem(destinationCategory.assumptions, destinationIndex, draggableActivity);

                const updatedSourceCategory = { ...sourceCategory, assumptions: updatedSourceActivities };
                const updatedDestinationCategory = { ...destinationCategory, assumptions: updatedDestinationActivities };

                const updatedCategories = [...categories];
                updatedCategories[sourceCategoryIndex] = updatedSourceCategory;
                updatedCategories[destinationCategoryIndex] = updatedDestinationCategory;
                setCategories(updatedCategories);
                updateCategoriesOrder(updatedCategories);
                updateExpandedCategories(updatedCategories);
            }
        }

        // Reordering categories
        if (type === 'droppable-category') {
            const updatedCategories = reorder(categories, sourceIndex, destinationIndex);
            setCategories(updatedCategories);
            updateCategoriesOrder(updatedCategories);
            updateExpandedCategories(updatedCategories);
        }
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const insertItem = (list, index, item) => {
        const result = Array.from(list);
        result.splice(index, 0, item);
        return result;
    };

    return (
        <div className="inbud-table_wrapper">
            <div className="tw-flex tw-flex-row tw-w-full tw-sticky tw-font-semibold">
                <div className="tw-w-full tw-bg-blue-25 tw-align-bottom tw-text-left tw-py-4">
                    {/*Header*/}
                    {/* 34px - drag handler column */}
                    <div className="tw-flex tw-ml-[34px]">
                        <div className="tw-basis-3/12 tw-px-2 tw-flex tw-items-center tw-gap-x-2">
                            <div className="tw-basis-4">
                                {anyExpandedCategory ? (
                                    <Tooltip placement="top" tooltipContent={$t('Collapse all')} tooltipType="text">
                                        <div className="tw-cursor-pointer">
                                            <CollapseAllIcon onClick={() => toggleAllExpanded()} className="tw-w-5 tw-h-5" />
                                        </div>
                                    </Tooltip>
                                ) : (
                                    <Tooltip placement="top" tooltipContent={$t('Expand all')} tooltipType="text">
                                        <div className="tw-cursor-pointer">
                                            <ExpandAllIcon onClick={() => toggleAllExpanded(true)} className="tw-w-5 tw-h-5 tw-cursor-pointer" />
                                        </div>
                                    </Tooltip>
                                )}
                            </div>
                            <label className="mb-0 font-weight-bold tw-text-sm">{$t('Assumption')}</label>
                        </div>
                        {showSourceColumn && <div className="tw-basis-3/12 tw-px-2 tw-flex tw-items-center">{$t('Source')}</div>}
                        <div
                            className={classNames('tw-px-2 tw-flex tw-items-center', { 'tw-basis-4/12': showSourceColumn, 'tw-basis-7/12': !showSourceColumn })}
                        >
                            <label className="mb-0 font-weight-bold tw-text-center tw-text-sm">{$t('Detailed Description')}</label>
                        </div>
                        <div className="tw-basis-1/12 tw-px-2 tw-flex tw-items-center">
                            <label className="mb-0 font-weight-bold tw-text-center tw-text-sm">{$t('Verified')}</label>
                        </div>
                        <div className="tw-basis-1/12 tw-px-2 tw-flex tw-items-center">
                            <label className="mb-0 font-weight-bold tw-text-center tw-text-sm">{$t('Actions')}</label>
                        </div>
                    </div>
                </div>
            </div>
            {/*Category Row*/}
            {!loading && !!groups && (
                <div className="flex-column flex-grow-1 overflow-y-auto overflow-x-hidden">
                    <div className="col-12 px-0">
                        <DragAndDrop onDragEnd={handleDragEnd}>
                            <Drop id="droppable" type="droppable-category" draggingOverClassNames="tw-bg-blue-25">
                                {groups.map((category, catIndex) => {
                                    const showCategory = expandedCategories[category.id] ? expandedCategories[category.id].expanded : true;

                                    return (
                                        <Drag
                                            key={`category-${category.id}`}
                                            id={`category-${category.id}`}
                                            index={catIndex}
                                            dragHandlerWrapperClasses="tw-h-9 tw-flex tw-items-center"
                                            dragHandlerClasses="tw-pt-0"
                                            className="tw-bg-[#f1f1f1] tw-border-t-2 tw-border-grey"
                                            dragHandlerColor="blue"
                                        >
                                            <CentralAssumptionsTableGroupRow
                                                key={category.id}
                                                assumptionGroup={category}
                                                onEdit={onAssumptionGroupEdit}
                                                onDelete={onAssumptionGroupDelete}
                                                canUpdate={props.canUpdate}
                                                canDelete={props.canDelete}
                                                index={catIndex}
                                                childIndex={null}
                                                toggleExpanded={() => toggleExpanded(category.id)}
                                                expanded={!deletable && showCategory}
                                                deletable={deletable}
                                                checkedForDeletion={deletable && deletableRows.has(category.id)}
                                                toggleDeletableRow={() => toggleDeletableRow(category.id)}
                                                showSourceColumn={showSourceColumn}
                                            ></CentralAssumptionsTableGroupRow>
                                            <div className={`tw-bg-slate-100 ${!deletable && showCategory ? '-tw-ml-[32px]' : 'tw-hidden'}`}>
                                                <Drop
                                                    key={`category-${category.id}`}
                                                    id={`category-${category.id}`}
                                                    type="droppable-category-activities"
                                                    draggingOverClassNames="tw-bg-blue-25"
                                                >
                                                    {category.assumptions?.map((activity, activityIndex) => {
                                                        return (
                                                            <Drag
                                                                key={`category-activity-${category.id.toString()}-${activity.id.toString()}`}
                                                                id={`category-activity-${category.id.toString()}-${activity.id.toString()}`}
                                                                index={activityIndex}
                                                                dragHandlerWrapperClasses="tw-h-9 tw-flex tw-items-center"
                                                                dragHandlerClasses="tw-pt-0"
                                                                dragHandlerColor="blue"
                                                                dragContainerClasses="tw-border-t-2 tw-border-grey"
                                                            >
                                                                <CentralAssumptionsTableRow
                                                                    key={`activity-${activity.id}`}
                                                                    assumption={activity}
                                                                    categoryIndex={catIndex}
                                                                    index={activityIndex}
                                                                    belongsTo={'category'}
                                                                ></CentralAssumptionsTableRow>
                                                            </Drag>
                                                        );
                                                    })}
                                                </Drop>
                                            </div>
                                        </Drag>
                                    );
                                })}
                            </Drop>
                        </DragAndDrop>
                    </div>
                </div>
            )}
            {loading && <div className="tw-w-full tw-bg-[#f1f1f1] tw-border-b-2 tw-border-grey tw-p-2">{$t('Loading')}...</div>}
            {!loading && groups?.length === 0 && (
                <div className="tw-w-full tw-bg-[#f1f1f1] tw-border-b-2 tw-border-grey tw-p-2">{$t('No central assumptions found')}.</div>
            )}
        </div>
    );
}
